import { useCallback, useEffect, useRef } from "react";
import type { TouchEvent } from "react";

/**
 * Long-press constants per [[selection-mode.spec]] § Behavior § Constants.
 * Mirrored in selection-mode-provider's docs and the spec changelog.
 */
export const LONG_PRESS_DURATION = 500;
export const LONG_PRESS_MOVE_THRESHOLD = 10;

interface LongPressHandlers {
  onTouchStart: (event: TouchEvent) => void;
  onTouchMove: (event: TouchEvent) => void;
  onTouchEnd: () => void;
  onTouchCancel: () => void;
}

/**
 * "Press and hold to select" for a record surface — the tile
 * ([[photo-grid-item.spec]] US-002) and the row ([[photo-list-item.spec]]
 * US-003) must behave identically, so the gesture lives here once.
 *
 * Fires `onLongPress(item)` after {@link LONG_PRESS_DURATION} ms, cancels if
 * the finger travels more than {@link LONG_PRESS_MOVE_THRESHOLD} px or a second
 * finger joins (that is a pinch, not a press), and vibrates on fire where
 * supported.
 *
 * The click that a touch device synthesises after the press still arrives, so
 * the consumer must call `consumeClick()` first in its click handler: it
 * returns true (and clears the flag) when the click is that trailing one, which
 * the consumer then ignores — otherwise one press would both select and open
 * the record.
 */
export function useLongPress<T>(
  item: T,
  onLongPress?: (item: T) => void,
): { longPressHandlers: LongPressHandlers; consumeClick: () => boolean } {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const didLongPressRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Clear any pending timer on unmount. Without this, a surface recycled by
  // the virtualizer mid-press would still fire its setTimeout after unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const onTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!onLongPress) return;
      // Multi-touch (pinch) — never start a long-press timer.
      if (e.touches.length >= 2) {
        clearTimer();
        return;
      }
      didLongPressRef.current = false;
      const touch = e.touches[0];
      startRef.current = { x: touch.clientX, y: touch.clientY };
      timerRef.current = setTimeout(() => {
        didLongPressRef.current = true;
        navigator.vibrate?.(50);
        onLongPress(item);
      }, LONG_PRESS_DURATION);
    },
    [onLongPress, clearTimer, item],
  );

  const onTouchMove = useCallback(
    (e: TouchEvent) => {
      // A second finger joined → cancel; this is a pinch.
      if (e.touches.length >= 2) {
        clearTimer();
        return;
      }
      if (!startRef.current) return;
      const touch = e.touches[0];
      const dx = touch.clientX - startRef.current.x;
      const dy = touch.clientY - startRef.current.y;
      if (Math.sqrt(dx * dx + dy * dy) > LONG_PRESS_MOVE_THRESHOLD) {
        clearTimer();
      }
    },
    [clearTimer],
  );

  const onTouchEnd = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const consumeClick = useCallback(() => {
    if (!didLongPressRef.current) return false;
    didLongPressRef.current = false;
    return true;
  }, []);

  return {
    longPressHandlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel: onTouchEnd,
    },
    consumeClick,
  };
}
